import { useCallback, useEffect, useState } from 'react';
import { toast } from 'sonner';
import {
    IndianRupee,
    Loader2,
    Check,
    AlertTriangle,
    Zap,
    Calendar,
    XCircle,
    CreditCard,
    Sparkles,
    ArrowRight,
} from 'lucide-react';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogDescription,
    DialogFooter,
} from '@/components/ui/dialog';
import { api } from '../lib/api';
import { useAuth } from '../lib/auth';
import { ScanPacksCard } from './ScanPacksCard';

const RZP_CHECKOUT_SRC = 'https://checkout.razorpay.com/v1/checkout.js';

/** Load Razorpay Checkout.js exactly once. */
function loadRazorpayCheckout() {
    return new Promise((resolve, reject) => {
        if (typeof window !== 'undefined' && window.Razorpay) return resolve(window.Razorpay);
        const existing = document.querySelector(`script[src="${RZP_CHECKOUT_SRC}"]`);
        if (existing) {
            existing.addEventListener('load', () => resolve(window.Razorpay));
            existing.addEventListener('error', reject);
            return;
        }
        const s = document.createElement('script');
        s.src = RZP_CHECKOUT_SRC;
        s.async = true;
        s.onload = () => resolve(window.Razorpay);
        s.onerror = () => reject(new Error('Failed to load Razorpay Checkout'));
        document.body.appendChild(s);
    });
}

function fmtInr(paise) {
    if (paise == null) return '\u2014';
    return new Intl.NumberFormat('en-IN', {
        style: 'currency',
        currency: 'INR',
        maximumFractionDigits: 0,
    }).format(paise / 100);
}

function fmtDate(iso) {
    if (!iso) return '\u2014';
    try {
        return new Date(iso).toLocaleDateString('en-IN', {
            day: 'numeric',
            month: 'short',
            year: 'numeric',
        });
    } catch {
        return iso.slice(0, 10);
    }
}

function daysUntil(iso) {
    if (!iso) return null;
    try {
        const diff = new Date(iso).getTime() - Date.now();
        return Math.max(0, Math.ceil(diff / (24 * 3600 * 1000)));
    } catch {
        return null;
    }
}

const STATUS_BADGE = {
    none: { label: 'Not subscribed', tone: 'muted' },
    created: { label: 'Trial active', tone: 'primary' },
    authenticated: { label: 'Trial active', tone: 'primary' },
    trialing: { label: 'Trial active', tone: 'primary' },
    active: { label: 'Active', tone: 'success' },
    past_due: { label: 'Payment failed', tone: 'danger' },
    halted: { label: 'Halted', tone: 'danger' },
    paused: { label: 'Paused', tone: 'warning' },
    cancel_at_period_end: { label: 'Ending soon', tone: 'warning' },
    cancelled: { label: 'Cancelled', tone: 'muted' },
    completed: { label: 'Completed', tone: 'muted' },
    expired: { label: 'Expired', tone: 'muted' },
};

const TONE_CLS = {
    muted: 'bg-muted text-muted-foreground border-border',
    primary: 'bg-primary/10 text-primary border-primary/20',
    success: 'bg-emerald-50 text-emerald-700 border-emerald-200',
    warning: 'bg-amber-50 text-amber-700 border-amber-200',
    danger: 'bg-rose-50 text-rose-700 border-rose-200',
};

export function BillingSection() {
    const { user } = useAuth();
    const [status, setStatus] = useState(null);
    const [invoices, setInvoices] = useState([]);
    const [loading, setLoading] = useState(true);
    const [starting, setStarting] = useState(false);
    const [cancelling, setCancelling] = useState(false);
    const [cancelOpen, setCancelOpen] = useState(false);

    const refresh = useCallback(async () => {
        try {
            const [s, inv] = await Promise.all([
                api.getBillingStatus(),
                api.listInvoices().catch(() => ({ invoices: [] })),
            ]);
            setStatus(s);
            setInvoices(inv?.invoices || []);
        } catch (e) {
            toast.error(e?.response?.data?.detail || 'Failed to load billing');
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => { refresh(); }, [refresh]);

    async function handleStartOrSubscribe() {
        setStarting(true);
        try {
            const s = await api.createSubscription();
            if (!s.subscription_id) throw new Error('Missing subscription id');
            const Razorpay = await loadRazorpayCheckout();
            const rzp = new Razorpay({
                key: s.key_id,
                subscription_id: s.subscription_id,
                name: 'Ease My Bill',
                description: `${s.plan_name || 'Ease My Bill Pro'} \u2014 1 month free trial`,
                image: '/favicon.ico',
                prefill: { email: user?.email || '' },
                theme: { color: '#0E7490' },
                handler: async () => {
                    toast.success('Trial started! First charge is 30 days away.');
                    await refresh();
                },
                modal: {
                    ondismiss: () => {
                        toast.info('Checkout closed. You can resume anytime.');
                    },
                },
                notes: { app: 'ease_my_bill' },
            });
            rzp.on('payment.failed', (resp) => {
                toast.error(`Payment failed: ${resp?.error?.description || 'unknown error'}`);
            });
            rzp.open();
        } catch (e) {
            toast.error(e?.response?.data?.detail || e.message || 'Could not start checkout');
        } finally {
            setStarting(false);
        }
    }

    async function handleCancel(atCycleEnd = true) {
        setCancelling(true);
        try {
            await api.cancelSubscription(atCycleEnd);
            toast.success(
                atCycleEnd
                    ? 'Subscription will end at your current period. Access continues till then.'
                    : 'Subscription cancelled immediately.'
            );
            setCancelOpen(false);
            await refresh();
        } catch (e) {
            const detail = e?.response?.data?.detail || e?.message || '';
            // Razorpay policy: subscriptions in `created` state (payment not yet
            // authenticated → still in trial with no billing cycle) cannot be
            // cancelled via API. Surface that clearly to the user.
            if (/no billing cycle/i.test(detail) || /cannot be cancelled/i.test(detail)) {
                toast.info(
                    'Your trial hasn\u2019t hit its first billing cycle yet, so there\u2019s nothing to charge and nothing to cancel. Simply don\u2019t authenticate payment when the trial ends and no charge will be made.',
                    { duration: 8000 }
                );
                setCancelOpen(false);
            } else {
                toast.error(detail || 'Failed to cancel');
            }
        } finally {
            setCancelling(false);
        }
    }

    if (loading) {
        return (
            <Card className="p-6 border-border/80" data-testid="settings-billing-card">
                <div className="flex items-center gap-2 text-sm text-muted-foreground">
                    <Loader2 size={14} className="animate-spin" /> Loading billing…
                </div>
            </Card>
        );
    }

    if (!status?.configured) {
        return (
            <Card className="p-6 border-border/80" data-testid="settings-billing-card">
                <div className="flex items-center gap-2 text-sm text-muted-foreground">
                    <AlertTriangle size={14} /> Billing is not configured on this server.
                </div>
            </Card>
        );
    }

    const badge = STATUS_BADGE[status.status] || STATUS_BADGE.none;
    const scanPct = status.scan_cap
        ? Math.min(100, Math.round((status.scans_used_this_month / status.scan_cap) * 100))
        : 0;
    const trialDaysLeft = daysUntil(status.trial_end);
    const hasSubscription = !!status.subscription_id;
    // 2026-07: unlimited-scan accounts (super-admin whitelist / internal test)
    // are treated as "permanently active" — no trial CTA, no scan meter, no
    // billing management UI. Just a soft badge saying "Unlimited access".
    const isUnlimited = !!status.unlimited_scans;
    const isFounding = !!status.founding_member;

    return (
        <>
        <Card className="p-6 border-border/80" data-testid="settings-billing-card">
            {/* Header */}
            <div className="flex items-start justify-between gap-3 mb-6">
                <div className="flex items-center gap-3">
                    <div className="h-9 w-9 rounded-lg bg-primary/10 text-primary flex items-center justify-center">
                        <CreditCard size={18} />
                    </div>
                    <div>
                        <h2 className="font-heading text-lg leading-tight" data-testid="billing-title">Billing</h2>
                        <p className="text-xs text-muted-foreground">Manage your Ease My Bill Pro subscription.</p>
                    </div>
                </div>
                <Badge
                    variant="outline"
                    className={`border ${TONE_CLS[badge.tone]} font-medium`}
                    data-testid="billing-status-badge"
                >
                    {badge.label}
                </Badge>
            </div>

            {/* --- Founding-member lifetime lock badge --- */}
            {isFounding && !isUnlimited && (
                <div
                    className="rounded-xl border-2 border-primary/40 bg-gradient-to-br from-primary/[0.08] to-primary/[0.02] p-4"
                    data-testid="billing-founding-block"
                >
                    <div className="flex items-start gap-3">
                        <div className="h-9 w-9 rounded-lg bg-primary text-primary-foreground grid place-items-center flex-shrink-0">
                            <span className="text-lg leading-none">🔥</span>
                        </div>
                        <div className="flex-1 min-w-0">
                            <div className="font-heading text-base mb-0.5">Founding member · ₹249/mo for life</div>
                            <p className="text-xs text-muted-foreground">
                                You&apos;re one of our first 100 subscribers — this price will never change for you, even if we raise prices later. Thank you for backing us early. 💛
                            </p>
                        </div>
                    </div>
                </div>
            )}

            {/* --- Unlimited-scans (super-admin whitelisted) --- */}
            {isUnlimited && (
                <div
                    className="rounded-xl border-2 border-primary/40 bg-primary/5 p-5"
                    data-testid="billing-unlimited-block"
                >
                    <div className="flex items-start gap-3">
                        <div className="h-9 w-9 rounded-lg bg-primary text-primary-foreground grid place-items-center flex-shrink-0">
                            <Sparkles size={18} />
                        </div>
                        <div className="flex-1 min-w-0">
                            <div className="font-heading text-xl mb-1">Unlimited access</div>
                            <p className="text-sm text-muted-foreground mb-3">
                                Your account has unlimited scans and no billing restrictions.
                                Nothing to manage here — scan as much as you like.
                            </p>
                            <div className="text-xs text-muted-foreground bg-background/60 rounded-md px-3 py-2 border border-border/60 inline-flex items-center gap-2">
                                <span className="text-primary font-medium">●</span>
                                {status.scans_used_this_month || 0} scan{status.scans_used_this_month === 1 ? '' : 's'} used this month · no cap
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* --- Not subscribed: CTA to start trial --- */}
            {!isUnlimited && (!hasSubscription || status.status === 'expired' || status.status === 'completed') && (
                <div className="rounded-xl border-2 border-primary/30 bg-primary/5 p-5" data-testid="billing-cta-block">
                    <div className="flex items-start gap-3">
                        <div className="h-9 w-9 rounded-lg bg-primary text-primary-foreground grid place-items-center flex-shrink-0">
                            <Sparkles size={18} />
                        </div>
                        <div className="flex-1">
                            <div className="font-heading text-xl mb-1">Start your 1-month free trial</div>
                            <p className="text-sm text-muted-foreground mb-4">
                                Get full access to Ease My Bill Pro — 150 bill scans/month, GST-aware alerts, Excel + Tally exports. Top-up packs available. No charge until day 31.
                            </p>
                            <div className="flex items-baseline gap-1 mb-4">
                                <IndianRupee size={18} />
                                <span className="font-heading text-3xl">399</span>
                                <span className="text-sm text-muted-foreground">/month after trial</span>
                            </div>
                            <Button
                                onClick={handleStartOrSubscribe}
                                disabled={starting}
                                size="lg"
                                className="gap-2"
                                data-testid="billing-start-trial-btn"
                            >
                                {starting ? <Loader2 size={16} className="animate-spin" /> : <Zap size={16} />}
                                {starting ? 'Opening Razorpay…' : 'Start free trial'}
                                <ArrowRight size={16} />
                            </Button>
                            <p className="text-[11px] text-muted-foreground mt-3">
                                Payment method will be authorised now but not charged for 30 days. Cancel anytime.
                            </p>
                        </div>
                    </div>
                </div>
            )}

            {/* --- Subscribed: full billing panel (skip for unlimited-scans accounts) --- */}
            {!isUnlimited && hasSubscription && !(status.status === 'expired' || status.status === 'completed') && (
                <div className="space-y-5" data-testid="billing-active-panel">
                    {/* Payment failed warning */}
                    {status.status === 'past_due' && (
                        <div className="rounded-lg border border-rose-200 bg-rose-50 p-3 flex items-start gap-2 text-sm text-rose-800" data-testid="billing-past-due-warning">
                            <AlertTriangle size={16} className="flex-shrink-0 mt-0.5" />
                            <div>
                                <div className="font-semibold">Payment failed</div>
                                <div className="text-xs">{status.last_payment_error || 'Please update your payment method.'}</div>
                            </div>
                        </div>
                    )}

                    {/* Trial countdown */}
                    {status.in_trial && (
                        <div className="rounded-lg border border-primary/20 bg-primary/5 p-3 flex items-center gap-2 text-sm text-primary" data-testid="billing-trial-banner">
                            <Sparkles size={16} />
                            <span className="font-medium">
                                Free trial — {trialDaysLeft} day{trialDaysLeft === 1 ? '' : 's'} remaining
                            </span>
                            <span className="text-muted-foreground text-xs ml-auto">
                                First charge on {fmtDate(status.trial_end)}
                            </span>
                        </div>
                    )}

                    {/* Cancel-pending banner */}
                    {status.cancel_at_period_end && (
                        <div className="rounded-lg border border-amber-200 bg-amber-50 p-3 flex items-center gap-2 text-sm text-amber-800" data-testid="billing-cancel-pending-banner">
                            <XCircle size={16} />
                            <span>
                                Your subscription will end on <b>{fmtDate(status.current_period_end)}</b>. You keep access until then.
                            </span>
                        </div>
                    )}

                    {/* Meta rows */}
                    <div className="grid sm:grid-cols-2 gap-4 text-sm">
                        <div>
                            <div className="text-xs uppercase tracking-wider text-muted-foreground mb-1">Plan</div>
                            <div className="font-medium">{status.plan_name}</div>
                            <div className="text-xs text-muted-foreground">{fmtInr(status.amount_paise)}/month</div>
                        </div>
                        <div>
                            <div className="text-xs uppercase tracking-wider text-muted-foreground mb-1">
                                {status.in_trial ? 'Trial ends' : 'Next billing'}
                            </div>
                            <div className="font-medium flex items-center gap-1.5">
                                <Calendar size={14} className="text-muted-foreground" />
                                {fmtDate(status.next_charge_at || status.current_period_end || status.trial_end)}
                            </div>
                        </div>
                    </div>

                    {/* Scan usage bar */}
                    <div>
                        <div className="flex items-center justify-between text-xs mb-1.5">
                            <div className="text-muted-foreground">Bill scans this month</div>
                            <div className="font-medium tabular-nums" data-testid="billing-scans-count">
                                {status.scans_used_this_month} / {status.scan_cap}
                            </div>
                        </div>
                        <Progress value={scanPct} className="h-2" data-testid="billing-scans-progress" />
                        <div className="text-[11px] text-muted-foreground mt-1">
                            Resets on the 1st of every month. Need more? <a href="mailto:support@easemybill.com" className="text-primary hover:underline">Contact us</a>.
                        </div>
                    </div>

                    {/* Actions */}
                    {!status.cancel_at_period_end && (
                        <div className="flex flex-wrap gap-3 pt-2 border-t border-border">
                            <Button
                                variant="outline"
                                size="sm"
                                onClick={() => setCancelOpen(true)}
                                disabled={cancelling}
                                data-testid="billing-cancel-btn"
                            >
                                Cancel subscription
                            </Button>
                        </div>
                    )}
                </div>
            )}

            {/* Payment history */}
            {invoices.length > 0 && (
                <div className="mt-6 pt-5 border-t border-border" data-testid="billing-invoices-section">
                    <div className="text-xs uppercase tracking-wider text-muted-foreground mb-3">Payment history</div>
                    <div className="rounded-lg border border-border overflow-hidden">
                        <table className="w-full text-sm">
                            <thead className="bg-muted/40 text-xs uppercase tracking-wider text-muted-foreground">
                                <tr>
                                    <th className="text-left px-3 py-2">Date</th>
                                    <th className="text-left px-3 py-2">Amount</th>
                                    <th className="text-left px-3 py-2">Method</th>
                                    <th className="text-left px-3 py-2">Payment ID</th>
                                </tr>
                            </thead>
                            <tbody>
                                {invoices.map((inv) => (
                                    <tr key={inv.payment_id} className="border-t border-border">
                                        <td className="px-3 py-2">{fmtDate(inv.captured_at)}</td>
                                        <td className="px-3 py-2 font-medium tabular-nums">{fmtInr(inv.amount_paise)}</td>
                                        <td className="px-3 py-2 capitalize text-muted-foreground">{inv.method || '\u2014'}</td>
                                        <td className="px-3 py-2 font-mono text-xs text-muted-foreground">{inv.payment_id}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>
            )}

            {/* Cancel confirmation dialog */}
            <Dialog open={cancelOpen} onOpenChange={setCancelOpen}>
                <DialogContent data-testid="billing-cancel-dialog">
                    <DialogHeader>
                        <DialogTitle>Cancel Ease My Bill Pro?</DialogTitle>
                        <DialogDescription>
                            Your access continues until <b>{fmtDate(status?.current_period_end || status?.trial_end)}</b>. You will not be charged again after that.
                        </DialogDescription>
                    </DialogHeader>
                    <div className="text-sm text-muted-foreground">
                        You can restart your subscription any time before that date. All your bills, alerts, and exports remain intact.
                    </div>
                    <DialogFooter className="gap-2">
                        <Button variant="ghost" onClick={() => setCancelOpen(false)} disabled={cancelling}>
                            Keep subscription
                        </Button>
                        <Button
                            variant="destructive"
                            onClick={() => handleCancel(true)}
                            disabled={cancelling}
                            data-testid="billing-cancel-confirm"
                        >
                            {cancelling ? <Loader2 size={14} className="animate-spin mr-1" /> : <Check size={14} className="mr-1" />}
                            Confirm cancel
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </Card>
        <ScanPacksCard status={status} onPurchased={refresh} />
        </>
    );
}
